import {
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material'
import { Close, ErrorOutline, Info, ModeEditOutline } from '@mui/icons-material'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import StarIcon from '@mui/icons-material/Star'
import { useEffect, useState } from 'react'
import MUIAutoComplete from '../../../customComponents/MUIAutoComplete'
import { AuthorizedFunctionSwitches } from './AuthorizedFunctionSwitches'
import { AuthFunctions } from '../../../../utils/permissions'
import { BlockButton } from '../../../customComponents/buttons/BlockButton'
import { BlueButton } from '../../../customComponents/buttons/BlueButton'
import { OrganizationType, type SecurityLevel, type SelectableOption, type UserPermission } from '../../../../utils/genericTypes'
import { WarningCard } from '../../../customComponents/WarningCard'
import {
  grantableFunctionRows,
  heldFunctionRows,
  selectionForRole,
  togglePermissionTreeFunction
} from './authorizedFunctionRows'
import { securityLevelsFor } from './securityLevels'
import { styles } from './OrganizationCard.styles'
import type { UserOrganization } from '../../../../models/UserOrganization'

export interface OrganizationCardChanges {
  buildingIds?: number[]
  functions?: string[]
  securityLevel?: SecurityLevel
  hasAccess?: boolean
}

interface Props {
  org: UserOrganization
  canSetDefault: boolean
  canEditBuildings: boolean
  canEditRole: boolean
  canEditFunctions: boolean
  canRemove?: boolean
  /** Admin view: every control is on screen and Save/Cancel appear on the first change, with no Edit button. */
  editsInline?: boolean
  isNew?: boolean
  onSetDefault: (orgId: number) => Promise<void>
  onSave?: (org: UserOrganization, changes: OrganizationCardChanges) => Promise<void>
  onCancel?: (org: UserOrganization) => void
  onRemove?: (org: UserOrganization) => void
  onDirtyChange?: (orgId: number, isDirty: boolean) => void
  loadBuildingOptions?: (orgId: number) => Promise<SelectableOption[]>
  /** The functions this membership holds, as permission keys, or null when they could not be read. */
  loadUserFunctions?: (orgId: number) => Promise<string[] | null>
  loadPermissionOptions?: (securityLevel: SecurityLevel) => Promise<UserPermission[]>
}

const sortByName = (options: SelectableOption[]): SelectableOption[] =>
  [...options].sort((a, b) => a.name.localeCompare(b.name))

const sameMembers = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value) => b.includes(value))

type FunctionsStatus = 'loading' | 'ready' | 'unavailable'

export const OrganizationCard = (props: Props): JSX.Element => {
  const grantsFromPermissionTree = props.loadPermissionOptions != null

  const [isEditing, setIsEditing] = useState(props.editsInline === true)
  const [isSaving, setIsSaving] = useState(false)
  const [buildings, setBuildings] = useState<SelectableOption[]>(props.org.buildings)
  const [availableBuildings, setAvailableBuildings] = useState<SelectableOption[]>([])
  const [heldFunctions, setHeldFunctions] = useState<string[]>([])
  const [selectedFunctions, setSelectedFunctions] = useState<string[]>([])
  const [functionsStatus, setFunctionsStatus] = useState<FunctionsStatus>(
    props.loadUserFunctions == null ? 'unavailable' : 'loading'
  )
  const [securityLevel, setSecurityLevel] = useState<SecurityLevel | undefined>(props.org.roleName)
  const [permissionOptions, setPermissionOptions] = useState<UserPermission[]>([])
  const [hasAccess, setHasAccess] = useState(true)
  const [pickerKey, setPickerKey] = useState(0)

  const canHaveBuildings = props.org.organizationType === OrganizationType.ISD ||
    props.org.organizationType === OrganizationType.District
  const editsBuildings = props.canEditBuildings && canHaveBuildings
  const canEdit = editsBuildings || props.canEditRole || props.canEditFunctions
  const roleOptions = props.canEditRole ? securityLevelsFor(props.org.organizationType) : []

  const isDirty = props.isNew === true ||
    !sameMembers(buildings.map((b) => b.name), props.org.buildings.map((b) => b.name)) ||
    !sameMembers(selectedFunctions, heldFunctions) ||
    securityLevel !== props.org.roleName ||
    !hasAccess

  useEffect(() => {
    props.onDirtyChange?.(props.org.id, isDirty)
  }, [isDirty])

  useEffect(() => {
    const loadCardData = async (): Promise<void> => {
      await Promise.all([
        props.editsInline === true ? loadBuildings() : Promise.resolve(),
        loadFunctions()
      ])
    }

    void loadCardData()
  }, [props.org.id])

  const loadBuildings = async (): Promise<void> => {
    if (props.loadBuildingOptions == null || !editsBuildings) return

    const options = await props.loadBuildingOptions(props.org.id)
    setAvailableBuildings(sortByName(options.filter((o) => !buildings.some((b) => b.id === o.id))))
  }

  // A grid built from an unresolved or failed read would claim the user holds nothing, which is a
  // worse answer than admitting it does not know.
  const loadFunctions = async (): Promise<void> => {
    if (props.loadUserFunctions == null) {
      setFunctionsStatus('unavailable')
      return
    }

    setFunctionsStatus('loading')

    const held = props.isNew === true ? [] : await props.loadUserFunctions(props.org.id)
    if (held == null) {
      setFunctionsStatus('unavailable')
      return
    }

    setHeldFunctions(held)
    setSelectedFunctions(held)

    if (props.loadPermissionOptions != null && props.org.roleName != null) {
      const options = await props.loadPermissionOptions(props.org.roleName)
      setPermissionOptions(options)
      if (props.isNew === true) setSelectedFunctions(selectionForRole(options))
    }

    setFunctionsStatus('ready')
  }

  const enterEditMode = async (): Promise<void> => {
    setIsEditing(true)
    await loadBuildings()
  }

  const leaveEditMode = (): void => {
    if (props.editsInline !== true) setIsEditing(false)
    setBuildings(props.org.buildings)
    setSecurityLevel(props.org.roleName)
    setHasAccess(true)

    // The grid is built from the role's permission tree, so restoring the role has to reload it.
    if (grantsFromPermissionTree) void loadFunctions()
    else setSelectedFunctions(heldFunctions)

    props.onCancel?.(props.org)
  }

  const addBuilding = (building: SelectableOption | null): void => {
    if (building == null) return

    setBuildings(sortByName([...buildings, building]))
    setAvailableBuildings(availableBuildings.filter((b) => b.id !== building.id))
    setPickerKey(pickerKey + 1)
  }

  const removeBuilding = (building: SelectableOption): void => {
    setBuildings(buildings.filter((b) => b.id !== building.id))
    setAvailableBuildings(sortByName([...availableBuildings, building]))
  }

  const toggleFunction = (functionName: string, checked: boolean): void => {
    setSelectedFunctions(togglePermissionTreeFunction(selectedFunctions, functionName, checked))
  }

  const changeRole = async (level: SecurityLevel): Promise<void> => {
    setSecurityLevel(level)

    if (props.loadPermissionOptions == null) return

    const options = await props.loadPermissionOptions(level)
    setPermissionOptions(options)
    setSelectedFunctions(selectionForRole(options))
  }

  const handleSave = async (): Promise<void> => {
    if (props.onSave == null) {
      setIsEditing(false)
      return
    }

    const changes: OrganizationCardChanges = {}
    if (editsBuildings) changes.buildingIds = buildings.map((b) => b.id)
    if (props.canEditFunctions) changes.functions = selectedFunctions
    if (props.canEditRole) {
      if (roleOptions.length > 0) changes.securityLevel = securityLevel
      if (props.canRemove !== true) changes.hasAccess = hasAccess
    }

    setIsSaving(true)
    await props.onSave(props.org, changes)
    setIsSaving(false)
    if (props.editsInline !== true) setIsEditing(false)
  }

  const rows = grantsFromPermissionTree
    ? grantableFunctionRows(permissionOptions, selectedFunctions)
    : heldFunctionRows(selectedFunctions)
  const participatesInCeta = functionsStatus === 'ready'
    ? selectedFunctions.includes(AuthFunctions.CETA_PARTICIPATION)
    : props.org.userFunctions.includes(AuthFunctions.CETA_PARTICIPATION)

  return <Box sx={styles.card} data-testid={`orgCard-${props.org.id}`}>
    <Box sx={styles.cardHeader}>
      <Box sx={styles.cardTitle}>
        {props.org.isDefault
          ? <StarIcon data-testid='defaultOrgIcon' />
          : <StarBorderIcon data-testid='nonDefaultOrgIcon' />}

        <Typography variant='h4'>{props.org.name}</Typography>
      </Box>

      <Box sx={styles.cardHeaderActions}>
        <FormControlLabel
          control={<Checkbox
            checked={props.org.isDefault}
            disabled={!props.canSetDefault || props.org.isDefault}
            onChange={() => { void props.onSetDefault(props.org.id) }}
            sx={styles.defaultCheckbox}
            data-testid='setAsDefault'
          />}
          label={<Typography variant='body1'>Set as default</Typography>}
        />

        {canEdit && props.editsInline !== true && !isEditing && <BlueButton
          onClick={() => { void enterEditMode() }}
          endIcon={<ModeEditOutline sx={{ fontSize: '16px' }} />}
          dataTestId='editOrg'
        >
          Edit
        </BlueButton>}

        {props.canRemove === true && props.onRemove != null && <BlockButton
          onClick={() => { props.onRemove?.(props.org) }}
          startIcon={<ErrorOutline sx={{ fontSize: '16px' }} />}
          color='var(--red-500)'
          sx={styles.removeButton}
          dataTestId='removeOrg'
        >
          Remove User
        </BlockButton>}
      </Box>
    </Box>

    <Divider />

    {!canEdit && <Box sx={styles.viewOnlyPill} data-testid='viewOnly'>
      <Info sx={styles.viewOnlyIcon} />
      <Typography variant='body1'>View Only</Typography>
    </Box>}

    {participatesInCeta && canHaveBuildings && buildings.length === 0 &&
      <WarningCard message='User must have a building for CETA purposes.' />}

    <Box sx={canHaveBuildings ? styles.columns : styles.singleColumn}>
      {canHaveBuildings && <Box sx={styles.column}>
        <Typography variant='h5'>Building Assignments</Typography>

        {isEditing && props.canEditBuildings && <MUIAutoComplete
          key={pickerKey}
          label='Buildings'
          name='activeBuildings'
          separateLabel={true}
          placeholder='Add a building'
          options={availableBuildings}
          onChange={(_e, value) => { addBuilding(value as SelectableOption) }}
        />}

        <Box sx={styles.chips}>
          {buildings.map((building) => (
            <Chip
              key={building.id}
              label={building.name}
              sx={styles.chip}
              onDelete={isEditing && props.canEditBuildings ? () => { removeBuilding(building) } : undefined}
              deleteIcon={<Close sx={{ fill: 'var(--grey-900)' }} data-testid={`remove-${building.name}`} />}
            />
          ))}

          {buildings.length === 0 && <Typography variant='body1' data-testid='noBuildings'>No buildings</Typography>}
        </Box>
      </Box>}

      <Box sx={styles.column}>
        <Typography variant='h5'>Access Privileges</Typography>

        {isEditing && props.canEditRole && props.canRemove !== true && <Box data-testid='grantAccess'>
          <FormLabel><Typography variant='body1'>Grant Access?</Typography></FormLabel>

          <RadioGroup
            value={hasAccess ? 'Yes' : 'No'}
            onChange={(e) => { setHasAccess(e.target.value === 'Yes') }}
          >
            <FormControlLabel value='Yes' control={<Radio data-testid='grantAccess-Yes' />} label={<Typography variant='body1'>Yes</Typography>} />
            <FormControlLabel value='No' control={<Radio data-testid='grantAccess-No' />} label={<Typography variant='body1'>No</Typography>} />
          </RadioGroup>
        </Box>}

        <Box>
          <Typography variant='body1'>Organization Role</Typography>

          {isEditing && roleOptions.length > 0
            ? <RadioGroup
                value={securityLevel ?? ''}
                onChange={(e) => { void changeRole(e.target.value as SecurityLevel) }}
                data-testid='organizationRoleOptions'
            >
              {roleOptions.map((level) => <FormControlLabel
                key={level}
                value={level}
                control={<Radio data-testid={`role-${level}`} />}
                label={<Typography variant='body1'>{level}</Typography>}
              />)}
            </RadioGroup>
            : <Typography variant='body1' sx={styles.roleValue} data-testid='organizationRole'>{props.org.roleName}</Typography>}
        </Box>

        <Box>
          <Typography variant='body1' sx={{ mb: '16px' }}>Authorized Functions</Typography>

          {functionsStatus === 'loading' &&
            <Typography variant='body1' data-testid='authorizedFunctionsLoading'>Loading authorized functions…</Typography>}

          {functionsStatus === 'unavailable' &&
            <Typography variant='body1' data-testid='authorizedFunctionsUnavailable'>
              Authorized functions could not be loaded.
            </Typography>}

          {functionsStatus === 'ready' && <AuthorizedFunctionSwitches
            rows={rows}
            disabled={!isEditing || !props.canEditFunctions}
            onChange={toggleFunction}
          />}
        </Box>
      </Box>
    </Box>

    {(props.editsInline === true ? isDirty : isEditing) && <Box sx={styles.actions} data-testid='orgCardActions'>
      <BlockButton onClick={leaveEditMode} dataTestId='cancelOrg'>Cancel</BlockButton>

      <BlueButton onClick={() => { void handleSave() }} disabled={isSaving} dataTestId='saveOrg'>
        Save Changes
      </BlueButton>
    </Box>}
  </Box>
}
